import React, {useState} from "react";
import _ from "underscore";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";

import IssueEditor from "common_components/issue_editor";

const styles = {
  actions: {
    zIndex: 0,
  },
  dialogContent: {
    position: "absolute",
    left: "50%",
    top: "30%",
    transform: "translate(-50%, -30%)",
    maxWidth: "1200px",
  },
  optionControl: {
    marginLeft: "auto",
    marginRight: "4rem",
    width: "16rem",
    zIndex: "0",
  },
  textField: {
    width: "100%",
    margin: "1rem 0",
    boxSizing: "border-box",
  },
};

const subissueOpenTypes = {
  always: "Always",
  issue_is_triggered: "Issue is triggered",
  issue_is_not_triggered: "Issue is not triggered",
};

const textfieldInputs = [
  "reason_template",
  "detailed_reason",
  "positive_reason",
  "detailed_positive_reason",
  "about_description",
  "email_template",
  "standard_language",
  "guidance",
  "positive_guidance",
];

function initState() {
  const state = {
    name: "New Issue",
    nameError: "",
    open_subissues_when: "issue_is_triggered",
    show_if_triggered_only: "always",
    hide_subissue_when_parent: "never",
    empty_parent_applicable_clauses: "all_clauses",
  };
  textfieldInputs.forEach(textfieldInput => {
    state[textfieldInput] = "";
  });
  return state;
}

const CreateIssueDialog = props => {
  const [state, setState] = useState(initState());
  const [issueEditorRef, setIssueEditorRef] = useState(null);

  const handleChange = field => event => {
    setState({
      ...state,
      [field]: event.target.value,
    });
  };

  const validateName = e => {
    if (e.target.value.length === 0) {
      setState({
        ...state,
        nameError: "You must set the issue name",
      });
    } else {
      setState({
        ...state,
        nameError: "",
      });
    }
  };

  const nameAlreadyExists = name => {
    const existingNames =
      props.issues?.map(issue => issue.name.toLowerCase()) || [];
    return existingNames.includes(name.toLowerCase());
  };

  const addNewIssueHandler = async () => {
    const {name} = state;
    if (name.toLowerCase() === "new issue" || nameAlreadyExists(name)) {
      return setState({
        ...state,
        nameError: "Please specify different issue name",
      });
    }

    if (!issueEditorRef) {
      return undefined;
    }

    const editorValues = await issueEditorRef.getValue();

    if (
      name.length === 0 ||
      !editorValues ||
      Object.keys(editorValues.problems).length > 0
    ) {
      return undefined;
    }

    const issueToCreate = {
      name,
      ..._.pick(state, [
        "display_name",
        "open_subissues_when",
        "show_if_triggered_only",
        "hide_subissue_when_parent",
        "empty_parent_applicable_clauses",
        ...textfieldInputs,
      ]),
      ...editorValues.issue,
    };

    props.addIssue(issueToCreate);
    props.onDismiss();
  };

  return (
    <Dialog
      open={true}
      onClose={props.onDismiss}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        style: {maxWidth: "1200px"},
      }}
    >
      <DialogTitle>Create new issue</DialogTitle>
      <DialogContent>
        <TextField
          label="Issue Name"
          error={Boolean(state.nameError)}
          helperText={state.nameError}
          value={state.name}
          fullWidth
          margin="normal"
          onChange={handleChange("name")}
          onBlur={validateName}
        />

        <IssueEditor
          ref={setIssueEditorRef}
          topics={props.topics}
          topicsById={props.topicsById}
          contractTypesById={props.contractTypesById}
          hideTriggeringLogicButtons={true}
          organisationId={props.organisationId}
          initialIssue={{
            issue_class_id: 1,
            issue_order: "",
            trigger_display: true,
            issue_type: "TOPIC_NOT_PRESENT",
            rules: {topic_id: null},
            contract_types: [],
            display_name: null,
            override_values: {},
            styling: {summary: {}},
            llm_settings: {},
          }}
          roles={props.roles}
          llmsEnabled={true}
        />

        <div style={{display: "flex", flexDirection: "column"}}>
          <FormControl margin="normal" style={styles.optionControl}>
            <InputLabel>Subissues should be open when</InputLabel>
            <Select
              value={state.open_subissues_when}
              onChange={handleChange("open_subissues_when")}
              label="Subissues should be open when"
            >
              {Object.keys(subissueOpenTypes).map(sot => (
                <MenuItem key={sot} value={sot}>
                  {subissueOpenTypes[sot]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl margin="normal" style={styles.optionControl}>
            <InputLabel>When to show</InputLabel>
            <Select
              value={state.show_if_triggered_only}
              onChange={handleChange("show_if_triggered_only")}
              label="When to show"
            >
              <MenuItem value="always">Always</MenuItem>
              <MenuItem value="ifTriggered">If triggered</MenuItem>
              <MenuItem value="ifNotTriggered">If not triggered</MenuItem>
            </Select>
          </FormControl>

          <FormControl margin="normal" style={styles.optionControl}>
            <InputLabel>Hide subissue when parent</InputLabel>
            <Select
              value={state.hide_subissue_when_parent}
              onChange={handleChange("hide_subissue_when_parent")}
              label="Hide subissue when parent"
            >
              <MenuItem value="never">Never</MenuItem>
              <MenuItem value="ifTriggered">Alert</MenuItem>
              <MenuItem value="ifNotTriggered">Non-alert</MenuItem>
            </Select>
          </FormControl>

          <FormControl margin="normal" style={styles.optionControl}>
            <InputLabel>Empty Parent Applicable Clauses</InputLabel>
            <Select
              value={state.empty_parent_applicable_clauses}
              onChange={handleChange("empty_parent_applicable_clauses")}
              label="Empty Parent Applicable Clauses"
            >
              <MenuItem value="all_clauses">All Clauses</MenuItem>
              <MenuItem value="triggered_clauses_only">
                Triggered Clauses Only
              </MenuItem>
            </Select>
          </FormControl>
        </div>
        {textfieldInputs.map(item => (
          <TextField
            key={item}
            label={item.split("_").join(" ")}
            value={state[item]}
            style={styles.textField}
            margin="normal"
            multiline
            minRows={1}
            maxRows={4}
            onChange={handleChange(item)}
          />
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={props.onDismiss} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={addNewIssueHandler}
          color="primary"
          variant="contained"
          className="save-button"
        >
          Create Issue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateIssueDialog;
